import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { useParams } from "react-router-dom";
import { lightenColor, siteMapColorHex } from "../utils/colors";
import { ArrowData } from "../contexts/DataContext";
import { MAP_ZINDEX_ARROW, MAP_ZINDEX_SELECTED } from "../data-styling";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";

interface MapArrowProps {
  arrow: ArrowData;
  editing: boolean;
  onSave?: (arrow: ArrowData) => void;
}

/**
 * MapArrow component renders a Google Maps Polyline with arrow styling
 */
export default function MapArrow({
  arrow,
  editing = false,
  onSave,
}: MapArrowProps) {
  const map = useMap();
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const pathListenersSetup = useRef<boolean>(false);
  const currentArrowRef = useRef<ArrowData>(arrow);

  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const selectedArrowId = useParams()?.arrowId;
  const arrowSelected =
    editing && !!(selectedArrowId && arrow.id === parseInt(selectedArrowId));

  // set this up so that doing a new onSave within handlePathEdit
  // can properly reference the latest arrow data
  // without having to setup/tear-down event listeners
  useEffect(() => {
    currentArrowRef.current = arrow;
  }, [arrow]);

  useEffect(() => {
    if (!map || !arrow.coords || arrow.coords.length < 2) return;

    // Create the polyline path from coordinates
    const path = arrow.coords.map(
      (coord) => new google.maps.LatLng(coord[0], coord[1]),
    );

    // Determine the stroke color based on selected state
    const baseColor = siteMapColorHex(arrow.color, "red");

    const strokeColor = arrowSelected ? lightenColor(baseColor, 20) : baseColor;

    // Create polyline with arrow styling
    const polyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: strokeColor,
      strokeOpacity: 1.0,
      strokeWeight: 3,
      draggable: arrowSelected || false,
      editable: arrowSelected || false,
      zIndex: arrowSelected ? MAP_ZINDEX_SELECTED : MAP_ZINDEX_ARROW,
      // Add arrow symbols
      icons: [
        {
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 4,
            strokeColor: arrow.color || strokeColor,
            strokeWeight: 2,
            fillColor: strokeColor,
            fillOpacity: 1,
          },
          offset: "100%",
        },
      ],
      map: map,
    });

    // Set cursor style based on editing and selected state
    polyline.set("cursor", arrowSelected ? "grab" : "default");

    polylineRef.current = polyline;

    // Add click handler for interaction
    google.maps.event.addListener(
      polyline,
      "click",
      (event: google.maps.MapMouseEvent) => {
        console.log("Arrow clicked:", arrow.id, event);

        if (editing) {
          navigate(arrowSelected ? `edit` : `edit/arrow/${arrow.id}`);
        }

        // Only handle clicks when in editing mode
        if (editing && onSave) {
          onSave(arrow);
        }
      },
    );

    // Cleanup function
    return () => {
      if (polylineRef.current) {
        google.maps.event.clearListeners(polylineRef.current, "click");

        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, arrowSelected, editing, onSave]);

  // Update polyline when arrow properties change
  // we want to make sure we do NOT update path values
  // at this level, as doing so resets event listeners
  useEffect(() => {
    if (polylineRef.current && arrow) {
      // Determine colors based on selected state
    const baseColor = siteMapColorHex(arrow.color, "red");
      const strokeColor = arrowSelected
        ? lightenColor(baseColor, 20)
        : baseColor;
      const strokeOpacity = 1.0;
      const strokeWeight = 3;
      const iconScale = 4;

      // Update styling and interactive properties
      polylineRef.current.setOptions({
        strokeColor: strokeColor,
        strokeOpacity,
        strokeWeight: strokeWeight,
        draggable: arrowSelected || false,
        editable: arrowSelected || false,
        zIndex: arrowSelected ? MAP_ZINDEX_SELECTED : MAP_ZINDEX_ARROW,
        icons: [
          {
            icon: {
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: iconScale,
              strokeColor: strokeColor,
              strokeWeight: 2,
              fillColor: strokeColor,
              fillOpacity: strokeOpacity,
            },
            offset: "100%",
          },
        ],
      });

      // Update cursor style based on editing and selected state
      polylineRef.current.set("cursor", arrowSelected ? "grab" : "default");
    }
  }, [arrow, arrowSelected, editing]);

  // Set up path edit listeners only when needed
  useEffect(() => {
    if (!polylineRef.current || !onSave) return;

    if (arrowSelected && !pathListenersSetup.current) {
      // Set up listeners when arrow becomes selected

      const handlePathEdit = () => {
        const path = polylineRef.current?.getPath();
        if (!path || !onSave) return;

        const newCoords: [number, number][] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          newCoords.push([point.lat(), point.lng()]);
        }

        // have to reference the current arrow data
        // from a ref vs having the useEffect update whenever
        // arrow data changes. we want to avoid thrashing
        // event listener setup / teardown but also need
        // to not have stale arrow data
        const updatedArrow: ArrowData = {
          ...currentArrowRef.current,
          coords: newCoords,
        };

        onSave(updatedArrow);
      };

      // Listen for path modifications
      const path = polylineRef.current.getPath();
      google.maps.event.addListener(path, "set_at", handlePathEdit);
      google.maps.event.addListener(path, "insert_at", handlePathEdit);
      google.maps.event.addListener(path, "remove_at", handlePathEdit);

      pathListenersSetup.current = true;
    } else if (!arrowSelected && pathListenersSetup.current) {
      // Clean up listeners when arrow is no longer selected
      const path = polylineRef.current.getPath();
      if (path) {
        google.maps.event.clearListeners(path, "set_at");
        google.maps.event.clearListeners(path, "insert_at");
        google.maps.event.clearListeners(path, "remove_at");
      }
      pathListenersSetup.current = false;
    }
  }, [arrowSelected, onSave]);

  // This component doesn't render any DOM elements directly
  // The polyline is rendered directly on the Google Map
  return null;
}
